import classNames from 'classnames';
import React from 'react';
import { FocusScope } from 'react-aria';
import type { DistributedOmit } from 'type-fest';
import closeIcon from '../icons/non-prefilled/16x16/clear.svg';
import usePreventBodyScrolling from '../utils/usePreventBodyScrolling';
import type { ButtonProps } from './Button';
import Button, { IconButton } from './Button';
import * as styles from './ModalPrimitive.module.scss';

interface ModalPrimitiveHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  close?: () => void;
  title: string;
  titleExtra?: React.ReactNode;
  titleId?: string;
}

const ModalPrimitiveHeader = React.forwardRef<
  HTMLDivElement,
  ModalPrimitiveHeaderProps
>(({ close, title, titleExtra, titleId, className, ...props }, ref) => (
  <div ref={ref} {...props} className={classNames(styles.title, className)}>
    <h2 className={styles.titleText} id={titleId}>
      {title} {titleExtra}
    </h2>
    {close && (
      <div className={styles.closeContainer}>
        <IconButton onClick={close} applyNegativeMargin aria-label="Close">
          {closeIcon}
        </IconButton>
      </div>
    )}
  </div>
));

const ModalPrimitiveDescription = React.forwardRef<
  HTMLDivElement,
  ModalPrimitiveRootProps
>(({ children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={classNames(styles.description, className)}
    >
      {children}
    </div>
  );
});

interface ModalPrimitiveRootProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  fullscreen?: boolean;
  width?: number;
  role?: 'dialog' | 'alertdialog';
}

const ModalPrimitiveRoot = React.forwardRef<
  HTMLDivElement,
  ModalPrimitiveRootProps
>(
  (
    { children, className, fullscreen, width, role = 'dialog', ...props },
    ref,
  ) => {
    usePreventBodyScrolling(true);

    return (
      <FocusScope contain autoFocus restoreFocus>
        <div
          ref={ref}
          role={role}
          aria-modal="true"
          style={{ ...props.style, width: fullscreen ? undefined : width }}
          {...props}
          className={classNames(
            styles.root,
            {
              [styles.fullscreen]: fullscreen,
            },
            className,
          )}
        >
          {children}
        </div>
      </FocusScope>
    );
  },
);

interface ModalPrimitiveFooterProps
  extends React.HTMLAttributes<HTMLDivElement> {
  right: React.ReactNode;
  left?: React.ReactNode;
}

const ModalPrimitiveFooter = React.forwardRef<
  HTMLDivElement,
  ModalPrimitiveFooterProps
>(({ right, left, className, ...props }, ref) => (
  <div ref={ref} {...props} className={classNames(styles.footer, className)}>
    <div>{left}</div>
    <div className={styles.right}>{right}</div>
  </div>
));

const ModalPrimitiveButton = React.forwardRef<
  HTMLButtonElement,
  DistributedOmit<ButtonProps, 'size'>
>(({ children, ...props }, ref) => (
  <Button ref={ref} {...props} size="regular">
    {children}
  </Button>
));

const ModalPrimitive = {
  Description: ModalPrimitiveDescription,
  Root: ModalPrimitiveRoot,
  Header: ModalPrimitiveHeader,
  Footer: ModalPrimitiveFooter,
  Button: ModalPrimitiveButton,
};

export default ModalPrimitive;
